import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CircularProgress, Container,
  Checkbox, FormControlLabel, Grid, MenuItem, Paper, Radio, RadioGroup,
  Stack, TextField, Typography
} from '@mui/material';

import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.tsx';
import { supabase } from '../supabaseClient.ts';

type Dog = {
  id: string;
  name: string;
  breed: string | null;
  direct_booking_allowed: boolean | null;
};

type BookingType = 'dagopvang' | 'logeren' | '';

type BookingResult = {
  reservation_id: string;
  reservation_status: string;
  option_expires_at: string | null;
  total_amount: number;
};


type TimeExtras = {
  early_arrival_fee: number;
  late_pickup_fee: number;
  departure_day_daycare_fee: number;
  departure_day_daycare: boolean;
  total_extra: number;
};

type PriceLine = {
  care_date: string | null;
  stay_date: string | null;
  dog_id: string;
  dog_name: string;
  dog_number: number;
  rate_type: string;
  base_rate: number;
  discount_percent: number;
  final_rate: number;
  description: string;
};

export const NewReservationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [familyId, setFamilyId] = useState('');
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [dogIds, setDogIds] = useState<string[]>([]);
  const [bookingType, setBookingType] = useState<BookingType>('');
  const [step, setStep] = useState(1);

  const [dayDate, setDayDate] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [arrivalTime, setArrivalTime] = useState('14:00');
  const [departureTime, setDepartureTime] = useState('10:00');

  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');

  const [available, setAvailable] =
    useState<boolean | null>(null);

  const [reason, setReason] = useState('');
  const [basePrice, setBasePrice] = useState<number | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [timeExtras, setTimeExtras] = useState<TimeExtras | null>(null);
  const [priceLines, setPriceLines] = useState<PriceLine[]>([]);
  const [departureDaycareLines, setDepartureDaycareLines] = useState<PriceLine[]>([]);

  const [bookingResult, setBookingResult] =
    useState<BookingResult | null>(null);

  useEffect(() => {
    let active = true;

    async function loadData() {
      if (!supabase || !user) {
        if (active) setLoading(false);
        return;
      }

      const { data: family, error: familyError } =
        await supabase
          .from('families')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

      if (!active) return;

      if (familyError || !family) {
        setError('Je klantprofiel kon niet worden geladen.');
        setLoading(false);
        return;
      }

      setFamilyId(family.id);

      const { data, error: dogError } =
        await supabase
          .from('dogs')
          .select(
            'id, name, breed, direct_booking_allowed'
          )
          .eq('family_id', family.id)
          .eq('active', true)
          .order('name');

      if (!active) return;

      if (dogError) {
        setError('Je hondgegevens konden niet worden geladen.');
        setLoading(false);
        return;
      }

      const loadedDogs = data ?? [];
      setDogs(loadedDogs);

      if (
        loadedDogs.length === 1 &&
        loadedDogs[0].direct_booking_allowed === true
      ) {
        setDogIds([loadedDogs[0].id]);
      }

      setLoading(false);
    }

    loadData();

    return () => {
      active = false;
    };
  }, [user]);

  const selectedDogs =
    dogs.filter((dog) => dogIds.includes(dog.id));

  const selectedDogNames =
    selectedDogs.map((dog) => dog.name).join(', ');

  const now = new Date();

  const minDate =
    `${now.getFullYear()}-` +
    `${String(now.getMonth() + 1).padStart(2, '0')}-` +
    `${String(now.getDate()).padStart(2, '0')}`;

  const step1Valid =
    dogIds.length > 0 &&
    !!bookingType &&
    selectedDogs.length === dogIds.length &&
    selectedDogs.every((dog) => dog.direct_booking_allowed === true);

  const step2Valid =
    bookingType === 'dagopvang'
      ? !!dayDate
      : bookingType === 'logeren'
        ? !!arrivalDate &&
          !!departureDate &&
          departureDate > arrivalDate &&
          !!arrivalTime &&
          !!departureTime
        : false;

  const resetResult = () => {
    setAvailable(null);
    setReason('');
    setBasePrice(null);
    setPrice(null);
    setTimeExtras(null);
    setPriceLines([]);
    setDepartureDaycareLines([]);
    setBookingResult(null);
  };

  const startDate =
    bookingType === 'dagopvang'
      ? dayDate
      : arrivalDate;

  const endDate =
    bookingType === 'dagopvang'
      ? dayDate
      : departureDate;

  const checkAvailability = async () => {
    if (!supabase || !familyId || !dogIds.length || !step2Valid) {
      return;
    }

    setChecking(true);
    setError('');
    resetResult();

    try {
      let allowed = false;
      let why = '';
      let total: number | null = null;

      if (bookingType === 'dagopvang') {
        const { data, error: checkError } =
          await supabase.rpc('can_book_daycare', {
            p_family_id: familyId,
            p_dog_ids: dogIds,
            p_date: dayDate
          });

        if (checkError) throw checkError;

        allowed = data?.[0]?.allowed === true;
        why = data?.[0]?.reason ?? '';

        if (allowed) {
          const { data: detailData, error: priceError } =
            await supabase.rpc(
              'calculate_daycare_price_details',
              {
                p_dog_ids: dogIds,
                p_date: dayDate
              }
            );

          if (priceError) throw priceError;

          const details = ((detailData ?? []) as any[]).map((line) => ({
            care_date: line.care_date ?? null,
            stay_date: line.stay_date ?? null,
            dog_id: String(line.dog_id),
            dog_name: String(line.dog_name ?? ''),
            dog_number: Number(line.dog_number ?? 0),
            rate_type: String(line.rate_type ?? ''),
            base_rate: Number(line.base_rate ?? 0),
            discount_percent: Number(line.discount_percent ?? 0),
            final_rate: Number(line.final_rate ?? 0),
            description: String(line.description ?? '')
          })) as PriceLine[];

          total = details.reduce((sum, line) => sum + line.final_rate, 0);
          setPriceLines(details);
          setDepartureDaycareLines([]);
          setBasePrice(total);
          setTimeExtras(null);
        }
      } else {
        const { data, error: checkError } =
          await supabase.rpc('can_book_overnight_detailed_v1', {
            p_family_id: familyId,
            p_dog_ids: dogIds,
            p_start_date: arrivalDate,
            p_end_date: departureDate
          });

        if (checkError) throw checkError;

        allowed = data?.[0]?.allowed === true;
        why = data?.[0]?.reason ?? '';

        if (allowed) {
          const [priceResult, extrasResult] = await Promise.all([
            supabase.rpc(
              'calculate_overnight_price_details',
              {
                p_dog_ids: dogIds,
                p_start_date: arrivalDate,
                p_end_date: departureDate
              }
            ),
            supabase.rpc(
              'calculate_overnight_time_extras_v1',
              {
                p_family_id: familyId,
                p_dog_ids: dogIds,
                p_start_date: arrivalDate,
                p_end_date: departureDate,
                p_arrival_time: arrivalTime,
                p_departure_time: departureTime
              }
            )
          ]);

          if (priceResult.error) throw priceResult.error;
          if (extrasResult.error) throw extrasResult.error;

          const details = ((priceResult.data ?? []) as any[]).map((line) => ({
            care_date: line.care_date ?? null,
            stay_date: line.stay_date ?? null,
            dog_id: String(line.dog_id),
            dog_name: String(line.dog_name ?? ''),
            dog_number: Number(line.dog_number ?? 0),
            rate_type: String(line.rate_type ?? ''),
            base_rate: Number(line.base_rate ?? 0),
            discount_percent: Number(line.discount_percent ?? 0),
            final_rate: Number(line.final_rate ?? 0),
            description: String(line.description ?? '')
          })) as PriceLine[];

          const overnightBase =
            details.reduce((sum, line) => sum + line.final_rate, 0);
          const extras = extrasResult.data?.[0] as TimeExtras | undefined;

          if (!details.length || !extras) {
            throw new Error('De prijs kon niet volledig worden berekend.');
          }

          const normalizedExtras: TimeExtras = {
            early_arrival_fee: Number(extras.early_arrival_fee || 0),
            late_pickup_fee: Number(extras.late_pickup_fee || 0),
            departure_day_daycare_fee: Number(extras.departure_day_daycare_fee || 0),
            departure_day_daycare: extras.departure_day_daycare === true,
            total_extra: Number(extras.total_extra || 0)
          };

          let departureDetails: PriceLine[] = [];

          if (normalizedExtras.departure_day_daycare) {
            const { data: departureData, error: departureError } =
              await supabase.rpc(
                'calculate_daycare_price_details',
                {
                  p_dog_ids: dogIds,
                  p_date: departureDate
                }
              );

            if (departureError) throw departureError;

            departureDetails = ((departureData ?? []) as any[]).map((line) => ({
              care_date: line.care_date ?? null,
              stay_date: line.stay_date ?? null,
              dog_id: String(line.dog_id),
              dog_name: String(line.dog_name ?? ''),
              dog_number: Number(line.dog_number ?? 0),
              rate_type: String(line.rate_type ?? ''),
              base_rate: Number(line.base_rate ?? 0),
              discount_percent: Number(line.discount_percent ?? 0),
              final_rate: Number(line.final_rate ?? 0),
              description: String(line.description ?? '')
            })) as PriceLine[];
          }

          setPriceLines(details);
          setDepartureDaycareLines(departureDetails);
          setBasePrice(overnightBase);
          setTimeExtras(normalizedExtras);
          total = overnightBase + normalizedExtras.total_extra;
        }
      }

      setAvailable(allowed);
      setReason(why);
      setPrice(total);
      setStep(3);
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message ||
          'De beschikbaarheid kon niet worden gecontroleerd. Probeer het opnieuw.'
      );
    } finally {
      setChecking(false);
    }
  };

  const createReservation = async (
    makeOption: boolean
  ) => {
    if (
      !supabase ||
      !familyId ||
      !dogIds.length ||
      !bookingType ||
      !startDate ||
      !endDate ||
      available !== true ||
      price === null
    ) {
      return;
    }

    setBooking(true);
    setError('');

    try {
      const { data, error: bookingError } =
        await supabase.rpc(
          'create_customer_reservation_v2',
          {
            p_family_id: familyId,
            p_dog_ids: dogIds,
            p_booking_type: bookingType,
            p_start_date: startDate,
            p_end_date: endDate,
            p_make_option: makeOption,
            p_arrival_time: bookingType === 'logeren' ? arrivalTime : null,
            p_departure_time: bookingType === 'logeren' ? departureTime : null
          }
        );

      if (bookingError) {
        throw bookingError;
      }

      const result = data?.[0] as
        | BookingResult
        | undefined;

      if (!result) {
        throw new Error(
          'Er is geen reserveringsresultaat ontvangen.'
        );
      }

      // Een definitieve klantboeking krijgt direct de Moneybird-offerte.
      // We wachten de aanroep bewust even af, zodat de browser hem zeker
      // verstuurt. Een Moneybird-fout maakt de reservering zelf NIET ongeldig:
      // de dagelijkse Moneybird-run blijft het vangnet.
      if (!makeOption) {
        try {
          const { data: quoteData, error: quoteError } =
            await supabase.functions.invoke(
              'moneybird-quote-start-v1',
              {
                body: {
                  action: 'start',
                  reservation_id: result.reservation_id
                }
              }
            );

          if (quoteError) {
            console.error(
              'De Moneybird-offerte kon niet direct worden gestart:',
              quoteError
            );
          } else {
            console.log(
              'Moneybird-offerte direct gestart:',
              quoteData
            );
          }
        } catch (quoteError) {
          console.error(
            'De Moneybird-offerte kon niet direct worden gestart:',
            quoteError
          );
        }
      }

      setBookingResult(result);
      setStep(4);
    } catch (err: any) {
      console.error(err);

      setError(
        err?.message ||
          'De reservering kon niet worden opgeslagen.'
      );
    } finally {
      setBooking(false);
    }
  };

  const money = (value: number) =>
    new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(new Date(`${value}T12:00:00`));

  const timeHour = (value: string) => value.slice(0, 2);
  const timeMinute = (value: string) => value.slice(3, 5);

  const setTimePart = (
    current: string,
    part: 'hour' | 'minute',
    value: string
  ) => {
    const hour = part === 'hour' ? value : timeHour(current);
    let minute = part === 'minute' ? value : timeMinute(current);

    // Ophalen mag maximaal om 18:00.
    if (hour === '18' && minute === '30') {
      minute = '00';
    }

    return `${hour}:${minute}`;
  };

  const arrivalHours = Array.from(
    { length: 16 },
    (_, index) => String(index + 8).padStart(2, '0')
  );

  const departureHours = Array.from(
    { length: 19 },
    (_, index) => String(index).padStart(2, '0')
  );

  const halfHourMinutes = ['00', '30'];

  const formatExpiry = (value: string) =>
    new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));

  const PriceBreakdown = () => {
    if (price === null || !priceLines.length) return null;

    const perDog = selectedDogs.map((dog) => {
      const lines = priceLines.filter((line) => line.dog_id === dog.id);
      const base = lines.reduce((sum, line) => sum + line.base_rate, 0);
      const final = lines.reduce((sum, line) => sum + line.final_rate, 0);
      const discount = Math.max(0, base - final);
      const discountPercent = Math.max(
        0,
        ...lines.map((line) => Number(line.discount_percent || 0))
      );

      return {
        dog,
        units: lines.length,
        base,
        final,
        discount,
        discountPercent
      };
    });

    const unitLabel =
      bookingType === 'dagopvang'
        ? 'dagopvang'
        : 'overnachting';

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mt: 2,
          boxShadow: 'none'
        }}
      >
        <Typography variant="h6">
          Prijsopbouw
        </Typography>

        <Stack spacing={1.25} sx={{ mt: 1.5 }}>
          {perDog.map((item) => (
            <Box key={item.dog.id}>
              <Stack
                direction="row"
                justifyContent="space-between"
                gap={2}
              >
                <Typography fontWeight={800}>
                  {item.dog.name}
                </Typography>
                <Typography fontWeight={800}>
                  {money(item.final)}
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                {item.units}{' '}
                {item.units === 1
                  ? unitLabel
                  : bookingType === 'dagopvang'
                    ? 'dagopvangdagen'
                    : 'overnachtingen'}
                {' · '}
                basis {money(item.base)}
              </Typography>

              {item.discount > 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {item.discountPercent}% gezinskorting: − {money(item.discount)}
                </Typography>
              )}
            </Box>
          ))}

          {bookingType === 'logeren' && timeExtras && (
            <>
              {timeExtras.early_arrival_fee > 0 && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Typography>
                    Vroeg brengen ({arrivalTime} uur)
                  </Typography>
                  <Typography fontWeight={800}>
                    {money(timeExtras.early_arrival_fee)}
                  </Typography>
                </Stack>
              )}

              {timeExtras.late_pickup_fee > 0 && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Typography>
                    Later ophalen ({departureTime} uur)
                  </Typography>
                  <Typography fontWeight={800}>
                    {money(timeExtras.late_pickup_fee)}
                  </Typography>
                </Stack>
              )}

              {timeExtras.departure_day_daycare_fee > 0 && (
                <Box
                  sx={{
                    mt: 0.5,
                    pt: 1.25,
                    borderTop: '1px dashed',
                    borderColor: 'divider'
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ mb: 1 }}
                  >
                    <Typography fontWeight={800}>
                      Dagopvang op vertrekdag
                    </Typography>
                    <Typography fontWeight={800}>
                      {money(timeExtras.departure_day_daycare_fee)}
                    </Typography>
                  </Stack>

                  <Stack spacing={1}>
                    {departureDaycareLines.map((line) => {
                      const discountAmount =
                        Math.max(0, line.base_rate - line.final_rate);

                      return (
                        <Box key={`departure-daycare-${line.dog_id}`}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            gap={2}
                          >
                            <Typography>
                              {line.dog_name}
                            </Typography>
                            <Typography fontWeight={800}>
                              {money(line.final_rate)}
                            </Typography>
                          </Stack>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            Dagopvang · basis {money(line.base_rate)}
                          </Typography>

                          {discountAmount > 0 && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {line.discount_percent}% gezinskorting: − {money(discountAmount)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </>
          )}

          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              pt: 1.5
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              gap={2}
            >
              <Typography variant="h6">
                Totaalprijs
              </Typography>
              <Typography
                variant="h4"
                fontWeight={800}
              >
                {money(price)}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#fffafb',
        py: { xs: 2, md: 4 }
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/mijn-pippaya')}
          sx={{ mb: 1 }}
        >
          Terug naar Mijn Pippaya
        </Button>

        <Typography
          variant="overline"
          color="text.secondary"
        >
          Mijn Pippaya · nieuwe reservering
        </Typography>

        <Typography variant="h3">
          Nieuwe reservering
        </Typography>

        <Typography
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          Kies je hond, het soort opvang en de gewenste datum.
        </Typography>

        <Paper
          variant="outlined"
          sx={{ p: 2, mt: 3, boxShadow: 'none' }}
        >
          <Grid container spacing={1}>
            {[
              '1. Hond(en) & opvang',
              '2. Datum',
              '3. Beschikbaarheid & prijs',
              '4. Bevestigen'
            ].map((label, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={label}
              >
                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    fontWeight:
                      step === index + 1 ? 800 : 500,
                    bgcolor:
                      step === index + 1
                        ? 'rgba(244,143,166,0.15)'
                        : 'transparent'
                  }}
                >
                  {label}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {loading && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ mt: 3 }}
          >
            <CircularProgress size={24} />
            <Typography>
              Je gegevens ophalen...
            </Typography>
          </Stack>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && step === 1 && (
          <>
            <Card sx={{ p: 3, mt: 3 }}>
              <Typography variant="h5">
                Voor welke hond(en) wil je reserveren?
              </Typography>

              <Alert severity="info" sx={{ mt: 2 }}>
                Je kunt meerdere honden uit hetzelfde gezin tegelijk selecteren.
                De prijs wordt automatisch voor alle gekozen honden samen berekend,
                inclusief de geldende korting voor een tweede gezinshond.
              </Alert>

              <Stack sx={{ mt: 2 }}>
                {dogs.map((dog) => {
                  const selectable = dog.direct_booking_allowed === true;
                  const checked = dogIds.includes(dog.id);

                  return (
                    <Paper
                      key={dog.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 1.5,
                        boxShadow: 'none',
                        opacity: selectable ? 1 : 0.65
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checked}
                            disabled={!selectable}
                            onChange={(event) => {
                              setDogIds((current) =>
                                event.target.checked
                                  ? [...current, dog.id]
                                  : current.filter((id) => id !== dog.id)
                              );
                              resetResult();
                            }}
                          />
                        }
                        label={
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <PetsRoundedIcon />

                            <Box>
                              <Typography fontWeight={800}>
                                {dog.name}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {dog.breed || 'Ras niet ingevuld'}
                              </Typography>

                              {!selectable && (
                                <Typography
                                  variant="body2"
                                  color="warning.main"
                                  sx={{ mt: 0.25 }}
                                >
                                  Deze hond kan niet rechtstreeks via Mijn Pippaya worden geboekt.
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        }
                      />
                    </Paper>
                  );
                })}
              </Stack>
            </Card>

            <Card sx={{ p: 3, mt: 3 }}>
              <Typography variant="h5">
                Welke opvang wil je?
              </Typography>

              <RadioGroup
                value={bookingType}
                onChange={(event) => {
                  setBookingType(
                    event.target.value as BookingType
                  );
                  resetResult();
                }}
                sx={{ mt: 2 }}
              >
                <FormControlLabel
                  value="dagopvang"
                  control={<Radio />}
                  label={
                    <Stack
                      direction="row"
                      spacing={1.5}
                    >
                      <CalendarMonthRoundedIcon />

                      <Box>
                        <Typography fontWeight={800}>
                          Dagopvang
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Opvang voor één dag.
                        </Typography>
                      </Box>
                    </Stack>
                  }
                />

                <FormControlLabel
                  value="logeren"
                  control={<Radio />}
                  label={
                    <Stack
                      direction="row"
                      spacing={1.5}
                    >
                      <HomeRoundedIcon />

                      <Box>
                        <Typography fontWeight={800}>
                          Logeren
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Eén of meerdere nachten.
                        </Typography>
                      </Box>
                    </Stack>
                  }
                />
              </RadioGroup>
            </Card>

            <Stack
              alignItems="flex-end"
              sx={{ mt: 3 }}
            >
              <Button
                variant="contained"
                size="large"
                disabled={!step1Valid}
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={() => setStep(2)}
              >
                Verder naar datum
              </Button>
            </Stack>
          </>
        )}

        {!loading && step === 2 && (
          <>
            <Card sx={{ p: 3, mt: 3 }}>
              <Typography variant="h5">
                {bookingType === 'dagopvang'
                  ? 'Kies de dag voor de dagopvang'
                  : 'Kies de periode voor het logeren'}
              </Typography>

              <Alert severity="info" sx={{ mt: 2 }}>
                Je reserveert voor{' '}
                <strong>{selectedDogNames}</strong>.
              </Alert>

              {bookingType === 'dagopvang' ? (
                <TextField
                  fullWidth
                  type="date"
                  label="Datum dagopvang"
                  value={dayDate}
                  onChange={(event) => {
                    setDayDate(event.target.value);
                    resetResult();
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    min: minDate
                  }}
                  sx={{ mt: 3 }}
                />
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{ mt: 1 }}
                >
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Aankomstdatum"
                      value={arrivalDate}
                      onChange={(event) => {
                        setArrivalDate(
                          event.target.value
                        );
                        setDepartureDate('');
                        resetResult();
                      }}
                      InputLabelProps={{
                        shrink: true
                      }}
                      inputProps={{
                        min: minDate
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Vertrekdatum"
                      value={departureDate}
                      onChange={(event) => {
                        setDepartureDate(
                          event.target.value
                        );
                        resetResult();
                      }}
                      disabled={!arrivalDate}
                      InputLabelProps={{
                        shrink: true
                      }}
                      inputProps={{
                        min: arrivalDate || minDate
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              {bookingType === 'logeren' && (
                <>
                  <Alert severity="info" sx={{ mt: 3 }}>
                    Brengen vanaf 14:00 uur en ophalen uiterlijk 10:00 uur zijn inbegrepen.
                    Eerder brengen kan vanaf 08:00 uur voor €2,50 per half uur. Ophalen na
                    10:00 uur kost €2,50 per half uur tot en met 12:00 uur. Kies je een
                    latere ophaaltijd, dan wordt dagopvang op de vertrekdag automatisch
                    binnen dezelfde reservering berekend. Ophalen kan uiterlijk 18:00 uur.
                  </Alert>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography fontWeight={800} sx={{ mb: 1 }}>
                        Brengtijd
                      </Typography>

                      <Grid container spacing={1}>
                        <Grid item xs={7}>
                          <TextField
                            select
                            fullWidth
                            label="Uur"
                            value={timeHour(arrivalTime)}
                            onChange={(event) => {
                              setArrivalTime(
                                setTimePart(arrivalTime, 'hour', event.target.value)
                              );
                              resetResult();
                            }}
                          >
                            {arrivalHours.map((hour) => (
                              <MenuItem key={hour} value={hour}>
                                {hour}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={5}>
                          <TextField
                            select
                            fullWidth
                            label="Minuten"
                            value={timeMinute(arrivalTime)}
                            onChange={(event) => {
                              setArrivalTime(
                                setTimePart(arrivalTime, 'minute', event.target.value)
                              );
                              resetResult();
                            }}
                          >
                            {halfHourMinutes.map((minute) => (
                              <MenuItem key={minute} value={minute}>
                                {minute}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.75 }}
                      >
                        Vanaf 08:00 uur; vanaf 14:00 uur inbegrepen.
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography fontWeight={800} sx={{ mb: 1 }}>
                        Ophaaltijd
                      </Typography>

                      <Grid container spacing={1}>
                        <Grid item xs={7}>
                          <TextField
                            select
                            fullWidth
                            label="Uur"
                            value={timeHour(departureTime)}
                            onChange={(event) => {
                              setDepartureTime(
                                setTimePart(departureTime, 'hour', event.target.value)
                              );
                              resetResult();
                            }}
                          >
                            {departureHours.map((hour) => (
                              <MenuItem key={hour} value={hour}>
                                {hour}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={5}>
                          <TextField
                            select
                            fullWidth
                            label="Minuten"
                            value={timeMinute(departureTime)}
                            onChange={(event) => {
                              setDepartureTime(
                                setTimePart(departureTime, 'minute', event.target.value)
                              );
                              resetResult();
                            }}
                          >
                            {(timeHour(departureTime) === '18'
                              ? ['00']
                              : halfHourMinutes
                            ).map((minute) => (
                              <MenuItem key={minute} value={minute}>
                                {minute}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.75 }}
                      >
                        Tot 10:00 uur inbegrepen; na 12:00 uur geldt dagopvang.
                        Uiterlijk 18:00 uur ophalen.
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}
            </Card>

            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 3 }}
            >
              <Button
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => setStep(1)}
              >
                Terug
              </Button>

              <Button
                variant="contained"
                disabled={!step2Valid || checking}
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={checkAvailability}
              >
                {checking
                  ? 'Controleren...'
                  : 'Controleer beschikbaarheid & prijs'}
              </Button>
            </Stack>
          </>
        )}

        {!loading && step === 3 && (
          <>
            <Card sx={{ p: 3, mt: 3 }}>
              <Typography variant="h5">
                Beschikbaarheid & prijs
              </Typography>

              <Typography sx={{ mt: 1 }}>
                <strong>{selectedDogNames}</strong>
                {' · '}
                {bookingType === 'dagopvang'
                  ? 'Dagopvang'
                  : 'Logeren'}
              </Typography>

              <Typography color="text.secondary">
                {bookingType === 'dagopvang'
                  ? formatDate(dayDate)
                  : `${formatDate(
                      arrivalDate
                    )} t/m ${formatDate(
                      departureDate
                    )}`}
              </Typography>

              {bookingType === 'logeren' && (
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Brengen {arrivalTime} uur · ophalen {departureTime} uur
                </Typography>
              )}

              {available === true && (
                <Alert
                  severity="success"
                  sx={{ mt: 3 }}
                >
                  <Typography fontWeight={800}>
                    Beschikbaar
                  </Typography>
                  {reason}
                </Alert>
              )}

              {available === false && (
                <Alert
                  severity="warning"
                  sx={{ mt: 3 }}
                >
                  <Typography fontWeight={800}>
                    Niet beschikbaar
                  </Typography>                  <Typography>
                    {reason ||
                      'Deze datum of periode kan niet worden gereserveerd.'}
                  </Typography>
                </Alert>
              )}

              {available === true && price !== null && (
                <PriceBreakdown />
              )}
            </Card>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              gap={2}
              sx={{ mt: 3 }}
            >
              <Button
                startIcon={<ArrowBackRoundedIcon />}
                disabled={booking}
                onClick={() => {
                  resetResult();
                  setStep(2);
                }}
              >
                Datum wijzigen
              </Button>

              <Button
                variant="contained"
                disabled={
                  available !== true ||
                  price === null ||
                  booking
                }
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={() => setStep(4)}
              >
                Verder naar bevestigen
              </Button>
            </Stack>
          </>
        )}

        {!loading && step === 4 && !bookingResult && (
          <>
            <Card sx={{ p: 3, mt: 3 }}>
              <Typography variant="h5">
                Bevestig je reservering
              </Typography>

              <Alert
                severity="success"
                icon={<CheckCircleRoundedIcon />}
                sx={{ mt: 2 }}
              >
                De beschikbaarheid is gecontroleerd.
              </Alert>

              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  mt: 2,
                  boxShadow: 'none'
                }}
              >
                <Stack spacing={1}>
                  <Typography>
                    <strong>Hond(en):</strong>{' '}
                    {selectedDogNames}
                  </Typography>

                  <Typography>
                    <strong>Opvang:</strong>{' '}
                    {bookingType === 'dagopvang'
                      ? 'Dagopvang'
                      : 'Logeren'}
                  </Typography>

                  <Typography>
                    <strong>Datum:</strong>{' '}
                    {bookingType === 'dagopvang'
                      ? formatDate(dayDate)
                      : `${formatDate(
                          arrivalDate
                        )} t/m ${formatDate(
                          departureDate
                        )}`}
                  </Typography>

                  {bookingType === 'logeren' && (
                    <Typography>
                      <strong>Tijden:</strong>{' '}
                      brengen {arrivalTime} uur · ophalen {departureTime} uur
                    </Typography>
                  )}

                </Stack>
              </Paper>

              {price !== null && (
                <PriceBreakdown />
              )}

              <Typography
                variant="h6"
                sx={{ mt: 3 }}
              >
                Wat wil je doen?
              </Typography>

              <Grid
                container
                spacing={2}
                sx={{ mt: 0.5 }}
              >
                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      height: '100%',
                      boxShadow: 'none'
                    }}
                  >
                    <AccessTimeRoundedIcon />

                    <Typography
                      variant="h6"
                      sx={{ mt: 1 }}
                    >
                      3 dagen optie
                    </Typography>

                    <Typography
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Houd deze plek drie dagen voor
                      jezelf vast.
                    </Typography>

                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      disabled={booking}
                      onClick={() =>
                        createReservation(true)
                      }
                      sx={{ mt: 2 }}
                    >
                      {booking
                        ? 'Bezig...'
                        : '3 dagen optie nemen'}
                    </Button>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      height: '100%',
                      boxShadow: 'none'
                    }}
                  >
                    <CheckCircleRoundedIcon />

                    <Typography
                      variant="h6"
                      sx={{ mt: 1 }}
                    >
                      Definitief reserveren
                    </Typography>

                    <Typography
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Bevestig deze opvang direct
                      definitief.
                    </Typography>

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={booking}
                      onClick={() =>
                        createReservation(false)
                      }
                      sx={{ mt: 2 }}
                    >
                      {booking
                        ? 'Bezig...'
                        : 'Definitief reserveren'}
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
            </Card>

            <Button
              sx={{ mt: 3 }}
              startIcon={<ArrowBackRoundedIcon />}
              disabled={booking}
              onClick={() => setStep(3)}
            >
              Terug
            </Button>
          </>
        )}

        {!loading && step === 4 && bookingResult && (
          <Card sx={{ p: 3, mt: 3 }}>
            <Alert
              severity="success"
              icon={<CheckCircleRoundedIcon />}
            >
              <Typography fontWeight={800}>
                {bookingResult.reservation_status ===
                'option'
                  ? 'Je 3-dagenoptie staat!'
                  : 'Je reservering is bevestigd!'}
              </Typography>

              {bookingResult.reservation_status ===
                'option' &&
                bookingResult.option_expires_at && (
                  <Typography sx={{ mt: 1 }}>
                    Je optie blijft geldig tot{' '}
                    <strong>
                      {formatExpiry(
                        bookingResult.option_expires_at
                      )}
                    </strong>.
                  </Typography>
                )}
            </Alert>

            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                mt: 2,
                boxShadow: 'none'
              }}
            >
              <Typography>
                <strong>{selectedDogNames}</strong>
              </Typography>

              <Typography color="text.secondary">
                {bookingType === 'dagopvang'
                  ? 'Dagopvang'
                  : 'Logeren'}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                {bookingType === 'dagopvang'
                  ? formatDate(dayDate)
                  : `${formatDate(
                      arrivalDate
                    )} t/m ${formatDate(
                      departureDate
                    )}`}
              </Typography>

              {bookingType === 'logeren' && (
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Brengen {arrivalTime} uur · ophalen {departureTime} uur
                </Typography>
              )}

              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ mt: 2 }}
              >
                {money(
                  Number(
                    bookingResult.total_amount
                  )
                )}
              </Typography>
            </Paper>

          </Card>
        )}
      </Container>
    </Box>
  );
};
                  